/**
 * Galerie promoțională pe pagina client: pe ecran doar titlul (buton); la tap overlay mare cu swipe, fără autoplay.
 */
import { sanitizeImageSrc } from '../utils/url.js?v=82.86';
import {
  isCustomTemplateActive,
  isConnectTemplateActive,
  isAlfaTemplateActive
} from './client-template-style.js?v=82.86';
import {
  buildGalleryZoomSlide,
  attachGallerySwipe
} from '../utils/image-gallery-zoom.js?v=82.86';

let detachOverlayListeners = null;
let overlayKeyHandler = null;
let promoGalleryOpenOverlay = null;

const DEFAULT_TRIGGER_LABEL = 'Promoții';

/** Connect / Alfa / Custom au card propriu de oferte — nu afișa și butonul generic. */
function isPromoSelfManagedTemplate(page = document.getElementById('clientPage')) {
  return (
    isConnectTemplateActive(page) ||
    isAlfaTemplateActive(page) ||
    isCustomTemplateActive(page)
  );
}

/** Ascunde forțat blocul generic „Ofertele săptămânii”. */
export function hideGenericPromoGallery() {
  const section = document.getElementById('clientPremiumSectionPromo');
  const wrap = document.getElementById('layoutBlockPromo');
  const trigger = document.getElementById('clientPromoGalleryTrigger');
  const label = document.getElementById('clientPromoGalleryTriggerLabel');
  [section, wrap].forEach((el) => {
    if (!el) return;
    el.style.setProperty('display', 'none', 'important');
    el.setAttribute('aria-hidden', 'true');
  });
  if (trigger) {
    trigger.style.setProperty('display', 'none', 'important');
    trigger.style.removeProperty('background');
    trigger.style.removeProperty('background-color');
    trigger.style.removeProperty('background-image');
  }
  // textul rămâne: butonul e oricum ascuns, iar la trecerea spre altă temă (Modele) eticheta
  // reapărea goală („Ofertele noastre” dispărea după Cloud → Universal Soft)
}

function ensureForeignPromoChromeHidden() {
  if (isPromoSelfManagedTemplate()) return;
  try {
    import('./client-connect-template.js?v=82.86')
      .then((m) => m.hideConnectOnlyChrome?.())
      .catch(() => {});
  } catch (_) {
    /* ignore */
  }
}

export function openClientPromoGalleryOverlay(startAt = 0) {
  if (typeof promoGalleryOpenOverlay !== 'function') return false;
  import('../services/client-analytics.js?v=82.86')
    .then((m) => m.trackClientEvent('images', 'open'))
    .catch(() => {});
  promoGalleryOpenOverlay(startAt);
  return true;
}

/** Link direct la o poză din secțiunea editabilă: /m/{slug}?oferta=3 (numărătoare de la 1).
 *  Ex.: un QR pe fiecare aparat din sală deschide direct exercițiul lui. */
export function openPromoFromUrl() {
  try {
    const m = /[?&]oferta=(\d{1,2})\b/.exec(location.search || '');
    if (!m) return false;
    const i = Math.max(0, parseInt(m[1], 10) - 1);
    let tries = 0;
    const tick = () => {
      if (openClientPromoGalleryOverlay(i)) return;
      if (++tries < 40) setTimeout(tick, 250);
    };
    tick();
    return true;
  } catch (_) { return false; }
}

function buildImages(settings) {
  const g = settings?.clientPromoGallery;
  if (!g || g.enabled !== true || !Array.isArray(g.images)) return [];
  return g.images.map((u) => String(u || '').trim()).filter(Boolean).slice(0, 10);
}

/** Linkul fiecărei poze (opțional, doar https), în aceeași ordine cu pozele. */
export function promoLinkAt(settings, i) {
  const g = settings?.clientPromoGallery;
  const raw = g && Array.isArray(g.links) ? String(g.links[i] || '').trim() : '';
  return /^https:\/\/[^\s<>"']+$/i.test(raw) ? raw : '';
}

/** Deschide linkul pozei i (clip YouTube, comandă, programare…) într-o filă nouă; întoarce true dacă există. */
export function openPromoLink(settings, i) {
  const url = promoLinkAt(settings, i);
  if (!url) return false;
  import('../services/client-analytics.js?v=82.86')
    .then((m) => m.trackClientEvent('images', 'link'))
    .catch(() => {});
  // <a> creat programatic: pe iOS Safari window.open din JS e blocat
  const a = document.createElement('a');
  a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
  document.body.appendChild(a); a.click(); a.remove();
  return true;
}

/** Platforma linkului → eticheta și iconița butonului „Deschide”. Iconițe generice (play, cameră, notă), nu logourile oficiale. */
function linkKind(url) {
  let h = '';
  try { h = new URL(url).hostname.replace(/^www\.|^m\./, ''); } catch (_) { /* ignore */ }
  const P = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">';
  if (/(^|\.)youtube\.com$|^youtu\.be$/.test(h)) return { id: 'youtube', label: 'Vezi pe YouTube', icon: `${P}<rect x="2" y="5" width="20" height="14" rx="5" fill="#FF0033"/><path d="M10 9l5 3-5 3z" fill="#fff"/></svg>` };
  if (/(^|\.)instagram\.com$/.test(h)) return { id: 'instagram', label: 'Vezi pe Instagram', icon: `${P}<rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="#E1306C" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="none" stroke="#E1306C" stroke-width="2"/><circle cx="17.3" cy="6.7" r="1.3" fill="#E1306C"/></svg>` };
  if (/(^|\.)tiktok\.com$/.test(h)) return { id: 'tiktok', label: 'Vezi pe TikTok', icon: `${P}<path d="M14 3v10.2a3.2 3.2 0 1 1-2.6-3.1V7.3a6 6 0 1 0 5.4 6V8.6c1.2.8 2.6 1.2 4 1.2V7a4.6 4.6 0 0 1-4-4z" fill="#111"/></svg>` };
  if (/(^|\.)facebook\.com$|^fb\.com$/.test(h)) return { id: 'facebook', label: 'Vezi pe Facebook', icon: `${P}<circle cx="12" cy="12" r="10" fill="#1877F2"/><path d="M13.3 20v-6h2l.4-2.5h-2.4V10c0-.7.3-1.2 1.3-1.2h1.2V6.6c-.2 0-1-.1-1.8-.1-1.8 0-3.1 1.1-3.1 3.2v1.8H8.9V14H11v6z" fill="#fff"/></svg>` };
  return { id: 'web', label: `Deschide ${h || 'linkul'}`, icon: `${P}<path d="M14 4h6v6m0-6L10 14M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" fill="none" stroke="#17201C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>` };
}

/** Numele fiecărei poze (opțional), în aceeași ordine cu pozele. */
function buildLabels(settings, n) {
  const g = settings?.clientPromoGallery;
  const raw = g && Array.isArray(g.labels) ? g.labels : [];
  return Array.from({ length: n }, (_, i) => String(raw[i] || '').trim().slice(0, 40));
}

function closeOverlayShell(overlay, trigger) {
  if (!overlay) return;
  overlay.setAttribute('hidden', '');
  overlay.classList.remove('is-open');
  overlay.setAttribute('aria-hidden', 'true');
  if (trigger) {
    trigger.setAttribute('aria-expanded', 'false');
  }
  document.body.classList.remove('client-promo-gallery-open');
}

function bindPromoOverlayShell({
  overlay,
  trigger,
  closeBtn,
  backdrop,
  openOverlay,
  closeOverlay,
  pointerCleanups = [],
  includeTriggerClick = true
}) {
  const onCloseClick = () => closeOverlay();
  const onBackdropClick = (e) => {
    if (e.target === backdrop) closeOverlay();
  };

  let onTriggerClick = null;
  if (includeTriggerClick && trigger) {
    onTriggerClick = (e) => {
      e.preventDefault();
      openClientPromoGalleryOverlay();
    };
    trigger.addEventListener('click', onTriggerClick);
  }

  closeBtn.addEventListener('click', onCloseClick);
  backdrop.addEventListener('click', onBackdropClick);

  overlayKeyHandler = (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      e.preventDefault();
      closeOverlay();
    }
  };
  document.addEventListener('keydown', overlayKeyHandler);

  promoGalleryOpenOverlay = openOverlay;

  detachOverlayListeners = () => {
    if (onTriggerClick && trigger) {
      trigger.removeEventListener('click', onTriggerClick);
    }
    closeBtn.removeEventListener('click', onCloseClick);
    backdrop.removeEventListener('click', onBackdropClick);
    if (overlayKeyHandler) {
      document.removeEventListener('keydown', overlayKeyHandler);
      overlayKeyHandler = null;
    }
    pointerCleanups.forEach((fn) => {
      try {
        fn();
      } catch (_) {
        /* ignore */
      }
    });
    promoGalleryOpenOverlay = null;
    closeOverlayShell(overlay, trigger);
  };
}

/**
 * Buton compact în #clientHeroActions; galeria cu swipe într-un overlay full-screen.
 */
export function applyClientPromoGalleryUI(settings) {
  if (detachOverlayListeners) {
    try {
      detachOverlayListeners();
    } catch (_) {
      /* ignore */
    }
    detachOverlayListeners = null;
  }
  if (overlayKeyHandler) {
    document.removeEventListener('keydown', overlayKeyHandler);
    overlayKeyHandler = null;
  }

  /* Cloud Dark: are cardul propriu „Oferte” — pregătim doar fereastra cu poze (fără butonul generic) */
  const cloudOffers = (!!document.getElementById('clientPage')?.classList.contains('template-cloud') &&
    !document.getElementById('clientPage')?.classList.contains('template-cloud-light')) ||
    /* Afiș / Tablă / Glass: cartonașe proprii cu pozele ofertelor; fereastra cu poze rămâne pregătită, butonul generic e ascuns din CSS */
    !!document.getElementById('clientPage')?.matches('.template-afis, .template-tabla, .template-glass');

  /* Connect / Alfa / Custom: un singur card de oferte (al lor) — fără dublură generică */
  if (isPromoSelfManagedTemplate() && !cloudOffers) {
    hideGenericPromoGallery();
    promoGalleryOpenOverlay = null;
    return;
  }

  if (!cloudOffers) ensureForeignPromoChromeHidden();

  const wrap = document.getElementById('layoutBlockPromo');
  const section = document.getElementById('clientPremiumSectionPromo');
  const trigger = document.getElementById('clientPromoGalleryTrigger');
  const triggerLabel = document.getElementById('clientPromoGalleryTriggerLabel');
  const overlay = document.getElementById('clientPromoGalleryOverlay');
  const overlayTitle = document.getElementById('clientPromoGalleryOverlayTitle');
  const overlayViewport = document.getElementById('clientPromoGalleryOverlayViewport');
  const overlayTrack = document.getElementById('clientPromoGalleryOverlayTrack');
  const overlayDots = document.getElementById('clientPromoGalleryOverlayDots');
  const closeBtn = document.getElementById('clientPromoGalleryOverlayClose');
  const backdrop = document.getElementById('clientPromoGalleryOverlayBackdrop');

  if (
    !wrap ||
    !trigger ||
    !triggerLabel ||
    !overlay ||
    !overlayTitle ||
    !overlayViewport ||
    !overlayTrack ||
    !overlayDots ||
    !closeBtn ||
    !backdrop
  ) {
    return;
  }

  closeOverlayShell(overlay, trigger);
  overlayTrack.replaceChildren();
  overlayDots.replaceChildren();
  overlayViewport.style.removeProperty('display');
  overlayDots.style.removeProperty('display');
  const overlayPanel = overlay.querySelector('.client-promo-gallery-overlay__panel');
  overlayViewport.style.removeProperty('height');
  overlayViewport.classList.remove('is-fitted');
  if (overlayPanel) { overlayPanel.style.removeProperty('width'); overlayPanel.classList.remove('is-fitted'); }
  promoGalleryOpenOverlay = null;
  trigger.style.removeProperty('background');
  trigger.style.removeProperty('background-color');
  trigger.style.removeProperty('background-image');

  const g = settings?.clientPromoGallery;
  const enabled = g?.enabled === true;
  const title = String(g?.title || '').trim();
  const images = buildImages(settings);

  if (!enabled) {
    hideGenericPromoGallery();
    overlayTitle.textContent = '';
    return;
  }

  if (images.length === 0) {
    wrap.style.setProperty('display', 'none', 'important');
    wrap.setAttribute('aria-hidden', 'true');
    if (section) section.style.setProperty('display', 'none', 'important');
    trigger.style.setProperty('display', 'none', 'important');
    triggerLabel.textContent = title || '';
    overlayTitle.textContent = title || '';
    overlayViewport.style.display = 'none';
    overlayDots.style.display = 'none';

    if (!title) return;

    function openTextOverlay() {
      overlay.removeAttribute('hidden');
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('client-promo-gallery-open');
    }

    function closeTextOverlay() {
      closeOverlayShell(overlay, trigger);
    }

    bindPromoOverlayShell({
      overlay,
      trigger,
      closeBtn,
      backdrop,
      openOverlay: openTextOverlay,
      closeOverlay: closeTextOverlay,
      includeTriggerClick: false
    });

    import('./client-template-style.js?v=82.86')
      .then((m) => {
        if (m.isDynamicTemplateActive?.()) {
          m.syncDynamicHeaderEditable?.(settings);
          m.syncDynamicCentralHero?.();
        }
        if (m.isFutureTemplateActive?.()) {
          m.syncFutureClientCopy?.(settings);
          m.syncFutureSocialStrip?.(settings);
        }
        if (m.isMinimalTemplateActive?.()) {
          m.syncMinimalOffersCard?.(settings);
        }
      })
      .catch(() => {});
    return;
  }

  const headline = title || DEFAULT_TRIGGER_LABEL;
  triggerLabel.textContent = headline;
  overlayTitle.textContent = headline;

  const zoomControllers = [];
  const slideImgs = [];

  images.forEach((url) => {
    const safe = sanitizeImageSrc(url);
    if (!safe) return;
    const { slide, zoom, img } = buildGalleryZoomSlide(safe, {
      slideClass: 'client-promo-gallery__slide',
      imgClass: 'client-promo-gallery__img client-promo-gallery__img--overlay',
      onLoad: (el) => { if (slideImgs[index] === el) fitPanelToImage(); }
    });
    zoomControllers.push(zoom);
    slideImgs.push(img);
    overlayTrack.appendChild(slide);
  });

  const n = overlayTrack.children.length;
  if (n === 0) {
    hideGenericPromoGallery();
    return;
  }

  const pct = 100 / n;
  overlayTrack.style.width = `${n * 100}%`;
  Array.from(overlayTrack.children).forEach((slide) => {
    slide.style.flex = `0 0 ${pct}%`;
  });

  if (section) {
    section.style.removeProperty('display');
    section.removeAttribute('aria-hidden');
  }
  wrap.style.removeProperty('display');
  wrap.style.display = 'block';
  wrap.removeAttribute('aria-hidden');
  trigger.style.removeProperty('display');
  trigger.style.display = 'flex';

  const dotEls = [];
  if (n > 1) {
    for (let i = 0; i < n; i++) {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'client-promo-gallery__dot';
      d.setAttribute('aria-label', `Imagine ${i + 1} din ${n}`);
      overlayDots.appendChild(d);
      dotEls.push(d);
    }
  }

  let index = 0;

  const resetAllZoom = () => {
    zoomControllers.forEach((z) => z.reset());
  };

  const labels = buildLabels(settings, n);
  const captionEl = document.getElementById('clientPromoGalleryOverlayCaption');
  const linkBtn = document.getElementById('clientPromoGalleryOverlayLink');
  if (linkBtn) linkBtn.__settings = settings; // setările curente (se reaplică la fiecare randare)
  if (linkBtn && linkBtn.dataset.bound !== '1') {
    linkBtn.dataset.bound = '1';
    linkBtn.addEventListener('click', () => openPromoLink(linkBtn.__settings || settings, Number(linkBtn.dataset.index) || 0));
  }
  function setTransform() {
    overlayTrack.style.transform = `translateX(calc(-100% * ${index} / ${n}))`;
    dotEls.forEach((d, i) => {
      d.classList.toggle('is-active', i === index);
    });
    if (captionEl) {
      const l = labels[index] || '';
      captionEl.textContent = l;
      captionEl.hidden = !l;
    }
    if (linkBtn) {
      const url = promoLinkAt(settings, index);
      linkBtn.hidden = !url;
      linkBtn.dataset.index = String(index);
      if (url) {
        const k = linkKind(url);
        linkBtn.className = `client-promo-gallery-overlay__link is-${k.id}`;
        linkBtn.innerHTML = `${k.icon}<span>${k.label}</span>`;
      }
    }
  }

  /* Fereastra se potrivește pe formatul pozei curente (4:5, 16:9, 1:1…): înălțimea viewport-ului și lățimea
     panoului se calculează din dimensiunile naturale ale pozei, în limitele ecranului. Fără benzi goale
     deasupra/dedesubt (object-fit: contain lăsa poza mică într-o ramă înaltă fixă). Temele care impun lățimea
     panoului cu !important (ex. Avva, foaie de jos) păstrează lățimea lor: se adaptează doar înălțimea. */
  let baseContentW = 0;
  let panelPadX = 0;
  let slidePadX = 0;
  let slidePadY = 0;
  function measurePanelBase() {
    if (!overlayPanel) return;
    overlayPanel.style.removeProperty('width');
    const cs = getComputedStyle(overlayPanel);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const bL = parseFloat(cs.borderLeftWidth) || 0;
    const bR = parseFloat(cs.borderRightWidth) || 0;
    panelPadX = padL + padR + bL + bR;
    baseContentW = overlayPanel.clientWidth - padL - padR;
    const slide = overlayTrack.firstElementChild;
    const ss = slide ? getComputedStyle(slide) : null;
    slidePadX = ss ? (parseFloat(ss.paddingLeft) || 0) + (parseFloat(ss.paddingRight) || 0) : 0;
    slidePadY = ss ? (parseFloat(ss.paddingTop) || 0) + (parseFloat(ss.paddingBottom) || 0) : 0;
  }
  function fitPanelToImage() {
    if (!overlayPanel || !overlay.classList.contains('is-open')) return;
    const img = slideImgs[index];
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    if (!baseContentW) measurePanelBase();
    if (!baseContentW) return;
    const vpH = overlayViewport.getBoundingClientRect().height;
    const chromeH = Math.max(0, overlayPanel.getBoundingClientRect().height - vpH); // titlu + puncte + spații
    const maxPanelH = Math.min(window.innerHeight * 0.92, 900);
    const availH = Math.max(120, maxPanelH - chromeH - slidePadY);
    const availW = Math.max(120, baseContentW - slidePadX);
    const scale = Math.min(availW / img.naturalWidth, availH / img.naturalHeight);
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    overlayViewport.classList.add('is-fitted');
    overlayPanel.classList.add('is-fitted');
    overlayViewport.style.height = `${h + slidePadY}px`;
    overlayPanel.style.width = `${w + slidePadX + panelPadX}px`;
  }

  const goTo = (i) => {
    resetAllZoom();
    index = Math.max(0, Math.min(n - 1, i));
    setTransform();
    fitPanelToImage();
  };

  setTransform();

  function openOverlay(startAt = 0) {
    overlay.removeAttribute('hidden');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('client-promo-gallery-open');
    baseContentW = 0;
    goTo(Number.isFinite(startAt) ? startAt : 0);
  }

  function closeOverlay() {
    closeOverlayShell(overlay, trigger);
  }

  const pointerCleanups = [];

  if (n > 1) {
    pointerCleanups.push(
      attachGallerySwipe(overlayViewport, {
        canSwipe: () => !zoomControllers[index]?.isZoomed?.(),
        onSwipePrev: () => goTo(index - 1),
        onSwipeNext: () => goTo(index + 1)
      })
    );

    dotEls.forEach((d, i) => {
      const onDot = () => goTo(i);
      d.addEventListener('click', onDot);
      pointerCleanups.push(() => d.removeEventListener('click', onDot));
    });
  }

  pointerCleanups.push(() => {
    zoomControllers.forEach((z) => z.destroy());
  });

  const onResize = () => { if (overlay.classList.contains('is-open')) { baseContentW = 0; fitPanelToImage(); } };
  window.addEventListener('resize', onResize);
  pointerCleanups.push(() => window.removeEventListener('resize', onResize));

  bindPromoOverlayShell({
    overlay,
    trigger,
    closeBtn,
    backdrop,
    openOverlay,
    closeOverlay,
    pointerCleanups,
    includeTriggerClick: true
  });

  if (cloudOffers) {
    // butonul generic rămâne ascuns; cardul „Oferte” al temei Cloud deschide fereastra
    [section, wrap, trigger].forEach((n) => n && n.style.setProperty('display', 'none', 'important'));
  }

  import('./client-template-style.js?v=82.86')
    .then((m) => {
      if (m.isDynamicTemplateActive?.()) {
        m.syncDynamicHeaderEditable?.(settings);
        m.syncDynamicCentralHero?.();
      }
      if (m.isMinimalTemplateActive?.()) {
        m.syncMinimalOffersCard?.(settings);
      }
    })
    .catch(() => {});
}
